import { SubscribeToDetails, UserDetails } from "../../Config/interface.config";
import { ReactComponent as GalleryIcon } from "./gallery-icon.svg";
import { ReactComponent as LinkIcon } from "./LinkIcon.svg";
import DefaultUserImg from "../../Assets/Images/user-profile.png";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../Redux/store";
import * as D from "../UserDetails/style";
import WhiteTick from "../../Assets/Icons/WhiteTick";
import { copyUserProfileLink, getCreatorProfilePath } from "../../Utils";
import { PiCrownSimpleLight } from "react-icons/pi";
import {
  checkIsSubscribe,
  DEFAULT_PRICING,
  isCreatorRole,
} from "../../Config/common.config";
import toast from "react-hot-toast";
import { AiOutlineMessage } from "react-icons/ai";
import { createMessageChannel } from "../../Redux/slices/messageSlice";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import EditIcon from "../../Assets/Icons/EditIcon";
import { getImageUrl } from "../../Utils/helpers";
import DefaultCoverImg from "../../Assets/Images/view-profile-cover.jpeg";

interface Props {
  user: UserDetails;
  isProfile?: boolean;
  subscribeCreator?: (creator: UserDetails) => void;
  authenticateUser?: () => void;
  isSubscribed?: boolean;
}

const ProfileHeader = ({
  user,
  isProfile = false,
  subscribeCreator,
  authenticateUser,
  isSubscribed: isSubscribedProp,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loggedInUser = useSelector((state: RootState) => state.user);
  const { image, fullname, totalPost, memberDetails } = user;
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [coverLoaded, setCoverLoaded] = useState(false);
  const [avatarLoaded, setAvatarLoaded] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const coverImageRef = useRef<HTMLImageElement | null>(null);
  const avatarImageRef = useRef<HTMLImageElement | null>(null);
  const coverImageUrl = user?.backgroundImage?.fileURL?.trim() || undefined;
  const profileImageUrl = image?.fileURL?.trim() || undefined;
  const creatorProfilePath = getCreatorProfilePath(user?.username);

  const goToEditProfile = () => {
    setProfileMenuOpen(false);
    navigate("/profile/edit");
  };

  const goToPreviewProfile = () => {
    setProfileMenuOpen(false);
    navigate(creatorProfilePath);
  };

  const onSubscribe = () => {
    if (!loggedInUser?.id) {
      toast("Please login or signup to complete your subscription");
      authenticateUser && authenticateUser();
    } else {
      subscribeCreator && subscribeCreator(user);
    }
  };

  const goToMessage = async () => {
    if (!loggedInUser?.id) {
      toast("Please log in or sign up to send a message to the creator.");
      authenticateUser && authenticateUser();
      return;
    }

    try {
      const response = await dispatch(
        createMessageChannel({
          from: loggedInUser.id,
          to: user?.id!,
          type: "direct",
        }),
      ).unwrap();
      const channelSlug = response?.data?.channelSlug || response?.channelSlug;
      navigate(`/messages`, {
        state: { userId: user.id, channelSlug, openChat: true },
      });
    } catch (error) {
      console.error("Failed to open message channel", error);
      toast.error("Unable to open messages. Please try again.");
    }
  };

  const isVideo = (url?: string) => {
    if (!url) return false;
    const extension = url.split(".").pop()?.toLowerCase();
    return ["mp4", "mov", "webm", "ogg"].includes(extension ?? "");
  };

  // A cached image can finish before a passive effect runs on mobile. Resolve
  // the current DOM image before paint so its faded state cannot get stuck.
  useLayoutEffect(() => {
    const currentImage = coverImageRef.current;
    setCoverLoaded(Boolean(currentImage?.complete && currentImage.naturalWidth > 0));
  }, [coverImageUrl]);

  useLayoutEffect(() => {
    const currentImage = avatarImageRef.current;
    setAvatarLoaded(Boolean(currentImage?.complete && currentImage.naturalWidth > 0));
  }, [profileImageUrl]);

  useEffect(() => {
    const isAlreadySubscribed = isSubscribedProp ?? !!(
      loggedInUser &&
      !isCreatorRole(loggedInUser.role) &&
      isCreatorRole(user.role) &&
      checkIsSubscribe(
        user.id as string,
        loggedInUser.subscribeTo as SubscribeToDetails[],
      )
    );
    setIsSubscribed(isAlreadySubscribed);
  }, [isSubscribedProp, user, loggedInUser]);

  useEffect(() => {
    if (!profileMenuOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!profileMenuRef.current?.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [profileMenuOpen]);

  return (
    <D.ProfileHeaderContainer>
      <div className="section-one" aria-busy={Boolean(coverImageUrl && !coverLoaded)}>
        {coverImageUrl &&
          (isVideo(coverImageUrl) ? (
            <video
              style={{
                position: "absolute",
                borderRadius: "10px",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                zIndex: 0,
              }}
              src={getImageUrl(coverImageUrl)}
              autoPlay
              loop
              muted
              playsInline
              onLoadedData={() => setCoverLoaded(true)}
            />
          ) : (
            <D.ProfileBackgroundImage
              ref={coverImageRef}
              src={getImageUrl(coverImageUrl)}
              alt=""
              loading="eager"
              decoding="async"
              data-loaded={coverLoaded}
              onLoad={() => setCoverLoaded(true)}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = DefaultCoverImg;
                setCoverLoaded(true);
              }}
            />
          ))}
      </div>

      <div className="section-two">
        <div className="flex items-start justify-between mt-3">
          <div className="flex items-start sm:gap-3 gap-2 min-w-0">
            {isVideo(profileImageUrl) ? (
              <D.ProfileImage
                ref={avatarImageRef}
                className="relative bottom-[4rem]"
                src={DefaultUserImg}
                data-loaded="true"
              />
            ) : (
              <D.ProfileImage
                ref={avatarImageRef}
                className="relative bottom-[4rem]"
                src={profileImageUrl ? getImageUrl(profileImageUrl) : DefaultUserImg}
                alt=""
                loading="eager"
                decoding="async"
                data-loaded={avatarLoaded}
                onLoad={() => setAvatarLoaded(true)}
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = DefaultUserImg;
                  setAvatarLoaded(true);
                }}
              />
            )}
            <div className="mb-1 min-w-0">
              <div className="flex gap-1 sm:gap-4 items-center">
                <h1 className="text-white font-medium font-inter text-lg sm:text-3xl truncate max-w-[44vw] sm:max-w-none">
                  {fullname}
                </h1>
                <WhiteTick key={"white"} />
              </div>
              {user?.username && (
                <p className="text-white opacity-[67%] font-inter font-light">
                  @{user?.username}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-start gap-4 shrink-0">
            <div className="hidden gap-5 sm:flex">
              <div className="flex gap-2">
                <GalleryIcon />
                <span className="text-white count-span">{totalPost}</span>
              </div>
            </div>
            {!isProfile && (
              <D.ProfileOwnerActions ref={profileMenuRef}>
                <D.ProfileEditTrigger
                  type="button"
                  aria-label="Profile actions"
                  aria-expanded={profileMenuOpen}
                  onClick={() => setProfileMenuOpen((open) => !open)}
                >
                  <EditIcon />
                </D.ProfileEditTrigger>

                {profileMenuOpen && (
                  <D.ProfileEditMenu>
                    <button type="button" onClick={goToEditProfile}>
                      Edit Profile
                    </button>
                    <span />
                    <button type="button" onClick={goToPreviewProfile}>
                      Preview Profile
                    </button>
                  </D.ProfileEditMenu>
                )}
              </D.ProfileOwnerActions>
            )}
          </div>
        </div>
      </div>

      <div className="section-three mb-[1rem]! mt-[-2.5rem] sm:mt-[-3rem]">
        {user.bio && <p className="text-white mb-0">{user.bio}</p>}
        <div
          className="cursor-pointer flex items-center gap-2 mt-3 sm:mt-4"
          onClick={() => copyUserProfileLink(user?.username ?? "")}
        >
          <LinkIcon className="profile-link-icon text-white cursor-pointer" />
          <span className="text-[#319DFF] text-sm sm:text-lg break-words w-full">
            {window.location.origin}{creatorProfilePath}
          </span>
        </div>

        {loggedInUser?.id !== user.id && (
          <div
            className={`flex items-center justify-center ${
              isSubscribed ? "mt-4" : "mt-6 sm:mt-7"
            } ${!isSubscribed ? "gap-4" : ""}`}
          >
            {!isSubscribed && (
              <D.JoinBtn onClick={() => onSubscribe()}>
                <PiCrownSimpleLight size={20} strokeWidth={"10px"} />
                Join For ₹{memberDetails?.monthlyPrice ?? DEFAULT_PRICING}
              </D.JoinBtn>
            )}
            <D.QuickActionMsgWeb onClick={goToMessage}>
              <AiOutlineMessage size={20} strokeWidth={"10px"} color="#000" />
              Message
            </D.QuickActionMsgWeb>
            <D.QuickAction onClick={goToMessage}>
              <AiOutlineMessage size={20} strokeWidth={"10px"} color="#000" />
              Message
            </D.QuickAction>
          </div>
        )}
      </div>
    </D.ProfileHeaderContainer>
  );
};

export default ProfileHeader;
